import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import type { Child } from "../api/types";
import { useAuth } from "../auth/AuthContext";

type ChildrenSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

export default function ChildrenSettingsDialog({ open, onClose }: ChildrenSettingsDialogProps) {
  const { child, setChildId, refreshContext } = useAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const loadChildren = useCallback(async () => {
    try {
      setError(null);
      const list = await api.listChildren();
      setChildren(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load children");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setNewName("");
    setEditingId(null);
    setEditingName("");
    setError(null);
    void loadChildren();
  }, [open, loadChildren]);

  async function addChild() {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const created = await api.createChild({ name });
      setNewName("");
      await loadChildren();
      await setChildId(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add child");
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit() {
    if (editingId == null) return;
    const name = editingName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateChild(editingId, { name });
      setEditingId(null);
      setEditingName("");
      await loadChildren();
      await refreshContext();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update child");
    } finally {
      setSaving(false);
    }
  }

  async function removeChild(target: Child) {
    const ok = window.confirm(
      `Remove ${target.name}? Their timetables, plans, and calendars will be permanently deleted.`,
    );
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteChild(target.id);
      const remaining = await api.listChildren();
      setChildren(remaining);
      if (child?.id === target.id) {
        if (remaining[0]) {
          await setChildId(remaining[0].id);
        } else {
          await refreshContext();
        }
      } else {
        await refreshContext();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to remove child");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography variant="subtitle2" color="text.secondary">
            Children
          </Typography>
          <Stack spacing={1.25}>
            {children.map((c) => {
              const isActive = child?.id === c.id;
              const isEditing = editingId === c.id;
              return (
                <Box
                  key={c.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: isActive ? "primary.main" : "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        label="Name"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        fullWidth
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void saveEdit();
                        }}
                      />
                      <Button size="small" onClick={() => setEditingId(null)} disabled={saving}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => void saveEdit()}
                        disabled={saving || !editingName.trim()}
                      >
                        Save
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                        {c.name}
                      </Typography>
                      {isActive && <Chip size="small" color="primary" label="Active" />}
                      <IconButton
                        size="small"
                        aria-label={`Edit ${c.name}`}
                        onClick={() => {
                          setEditingId(c.id);
                          setEditingName(c.name);
                        }}
                        disabled={saving}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${c.name}`}
                        onClick={() => void removeChild(c)}
                        disabled={saving || children.length <= 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}                </Box>
              );
            })}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
            <TextField
              size="small"
              label="Add child"
              placeholder="Child's name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") void addChild();
              }}
            />
            <Button
              variant="outlined"
              onClick={() => void addChild()}
              disabled={saving || !newName.trim()}
              sx={{ flexShrink: 0 }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
